/**
 * RN Voice Command Agent Component
 * 
 * Voice-First Clinical Documentation Interface for Hospice RNs
 * Features:
 * - Full-screen voice documentation interface
 * - Smart template detection and loading
 * - Live transcription with section mapping
 * - Order drafting, medication management
 * - IDG notes and care plan updates
 * - CMS-compliant documentation
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";
import {
  Mic, MicOff, Loader2, CheckCircle, AlertTriangle, X, 
  FileText, Pill, Stethoscope, ClipboardList, Heart, 
  Save, Send, ChevronDown, ChevronRight, Sparkles,
  Play, Square, Volume2, Edit, Trash2, Plus, Clock,
  Activity, Users, RefreshCw, Settings, HelpCircle,
  ListTodo, AlertCircle, CheckCircle2, XCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Template type configurations
const TEMPLATE_TYPES = {
  routine: { label: "Routine Visit", color: "bg-blue-500", icon: Stethoscope },
  prn: { label: "PRN/Symptom", color: "bg-orange-500", icon: AlertTriangle },
  hope_update: { label: "HOPE Update", color: "bg-purple-500", icon: Activity },
  wound: { label: "Wound Care", color: "bg-red-500", icon: Heart },
  pain: { label: "Pain Focused", color: "bg-amber-500", icon: Activity },
  death: { label: "Pronouncement", color: "bg-gray-500", icon: Clock }
};

// Status configurations for compliance
const SECTION_STATUS = {
  complete: { color: "text-green-500", icon: CheckCircle2 },
  incomplete: { color: "text-yellow-500", icon: AlertCircle },
  empty: { color: "text-gray-400", icon: XCircle }
};

export default function RNVoiceCommand({ patientId, patientName, onClose }) {
  const { isDark } = useTheme();
  
  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [sessionType, setSessionType] = useState("documentation");
  const [sessionActive, setSessionActive] = useState(false);
  
  // Template state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateSections, setTemplateSections] = useState([]);
  const [sectionContent, setSectionContent] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [liveTranscript, setLiveTranscript] = useState("");
  
  // AI detection state
  const [templateDetection, setTemplateDetection] = useState(null);
  const [sectionMapping, setSectionMapping] = useState(null);
  const [clarificationNeeded, setClarificationNeeded] = useState([]);
  const [cmsWarnings, setCmsWarnings] = useState([]);
  
  // Orders, medications, IDG, care plan state
  const [draftedOrders, setDraftedOrders] = useState([]);
  const [medicationUpdates, setMedicationUpdates] = useState([]);
  const [idgNotes, setIdgNotes] = useState([]);
  const [carePlanUpdates, setCarePlanUpdates] = useState([]);
  
  // Dialog states
  const [orderDialog, setOrderDialog] = useState(false);
  const [medicationDialog, setMedicationDialog] = useState(false);
  const [helpDialog, setHelpDialog] = useState(false);
  const [summaryDialog, setSummaryDialog] = useState(false);
  
  // Form states
  const [orderForm, setOrderForm] = useState({
    order_text: "",
    order_type: "medication",
    urgency: "routine",
    clinical_justification: ""
  });
  const [medicationForm, setMedicationForm] = useState({
    action: "start",
    medication_name: "",
    dose: "",
    frequency: "",
    route: "",
    indication: ""
  });
  
  // UI state
  const [activeTab, setActiveTab] = useState("documentation");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const durationIntervalRef = useRef(null);
  const activeSection = useRef(null);

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem("hospice_token");
    return token ? { "Authorization": `Bearer ${token}` } : {};
  };

  // Fetch available templates
  const fetchTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/rn-voice-command/templates`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  }, []);

  // Start voice session
  const startSession = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/rn-voice-command/session/start`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          patient_id: patientId || "demo-patient",
          session_type: sessionType
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        setSessionActive(true);
        toast.success("RN Command session started");
      } else {
        throw new Error("Failed to start session");
      }
    } catch (error) {
      console.error("Error starting session:", error);
      // Demo mode
      setSessionId(`demo_session_${Date.now()}`);
      setSessionActive(true);
      toast.success("Session started (demo mode)");
    } finally {
      setLoading(false);
    }
  };

  // Select and load template
  const selectTemplate = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/api/rn-voice-command/templates/${templateId}`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setSelectedTemplate(data.template);
        setTemplateSections(data.template.sections || []);
        
        // Initialize section content
        const initial = {};
        data.template.sections.forEach(s => {
          initial[s.id] = "";
        });
        setSectionContent(initial);
        
        // Set template in session
        if (sessionId) {
          await setSessionTemplate(templateId);
        }
        
        toast.success(`Template loaded: ${data.template.name}`);
      }
    } catch (error) {
      console.error("Error loading template:", error);
      toast.error("Failed to load template");
    }
  };

  // Set template in session
  const setSessionTemplate = async (templateId) => {
    try {
      const formData = new FormData();
      formData.append("template_id", templateId);
      
      await fetch(`${API_URL}/api/rn-voice-command/session/${sessionId}/set-template`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: formData
      });
    } catch (error) {
      console.error("Error setting template:", error);
    }
  };

  // Start voice recording
  const startRecording = async () => {
    if (!sessionActive) {
      toast.error("Please start a session first");
      return;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);
      
      // Start duration timer
      durationIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      
      toast.info("Recording started...");
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Microphone access denied");
    }
  };

  // Stop voice recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    }
  };

  // Transcribe audio
  const transcribeAudio = async (audioBlob) => {
    setLoading(true);
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        try {
          const res = await fetch(`${API_URL}/api/rn-voice-command/transcribe-base64`, {
            method: "POST",
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              session_id: sessionId,
              audio_base64: base64Audio,
              context: activeSection.current ? `Section: ${activeSection.current}` : null
            })
          });
          
          if (res.ok) {
            const data = await res.json();
            
            // Update transcript
            setTranscript(prev => prev + (prev ? " " : "") + data.transcript);
            setLiveTranscript(data.transcript);
            
            // Handle template detection
            if (data.template_detection && data.template_detection.detected_template) {
              setTemplateDetection(data.template_detection);
              
              if (!selectedTemplate && data.template_detection.confidence >= 70) {
                // Auto-load detected template
                await selectTemplate(data.template_detection.detected_template);
              }
            }
            
            // Handle section mapping
            if (data.section_mapping) {
              setSectionMapping(data.section_mapping);
              
              // Update section content
              if (data.section_mapping.mapped_sections) {
                setSectionContent(prev => ({
                  ...prev,
                  ...Object.fromEntries(
                    Object.entries(data.section_mapping.mapped_sections)
                      .map(([key, value]) => [key, prev[key] ? prev[key] + " " + value : value])
                  )
                }));
              }
              
              // Set warnings and clarifications
              if (data.section_mapping.clarification_needed) {
                setClarificationNeeded(data.section_mapping.clarification_needed);
              }
              if (data.section_mapping.cms_warnings) {
                setCmsWarnings(data.section_mapping.cms_warnings);
              }
            }
            
            toast.success("Transcription complete");
          }
        } catch (err) {
          console.error("Transcription API error:", err);
          // Demo mode fallback
          setTranscript(prev => prev + " [Demo transcription - microphone captured but API unavailable]");
          toast.info("Transcription processed (demo mode)");
        }
        
        setLoading(false);
      };
    } catch (error) {
      console.error("Error transcribing:", error);
      setLoading(false);
      toast.error("Transcription failed");
    }
  };

  // Draft physician order
  const draftOrder = async () => {
    if (!orderForm.order_text) {
      toast.error("Please enter order details");
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/rn-voice-command/orders/draft`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          session_id: sessionId,
          patient_id: patientId || "demo-patient",
          ...orderForm
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setDraftedOrders(prev => [...prev, data]);
        setOrderDialog(false);
        setOrderForm({
          order_text: "",
          order_type: "medication",
          urgency: "routine",
          clinical_justification: ""
        });
        toast.success("Order drafted - Pending MD Review");
      }
    } catch (error) {
      console.error("Error drafting order:", error);
      // Demo mode
      setDraftedOrders(prev => [...prev, {
        order_id: `demo_order_${Date.now()}`,
        status: "draft_pending_md",
        ...orderForm
      }]);
      setOrderDialog(false);
      toast.success("Order drafted (demo mode)");
    } finally {
      setLoading(false);
    }
  };

  // Process medication update
  const processMedication = async () => {
    if (!medicationForm.medication_name) {
      toast.error("Please enter medication name");
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/rn-voice-command/medications/update`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          session_id: sessionId,
          patient_id: patientId || "demo-patient",
          ...medicationForm
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setMedicationUpdates(prev => [...prev, data]);
        setMedicationDialog(false);
        setMedicationForm({
          action: "start",
          medication_name: "",
          dose: "",
          frequency: "",
          route: "",
          indication: ""
        });
        toast.success(`Medication ${medicationForm.action} recorded`);
      }
    } catch (error) {
      console.error("Error processing medication:", error);
      // Demo mode
      setMedicationUpdates(prev => [...prev, {
        update_id: `demo_med_${Date.now()}`,
        status: "pending_review",
        ...medicationForm
      }]);
      setMedicationDialog(false);
      toast.success("Medication recorded (demo mode)");
    } finally {
      setLoading(false);
    }
  };

  // Save draft
  const saveDraft = async () => {
    if (!selectedTemplate) {
      toast.error("Please select a template first");
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/rn-voice-command/documentation/save-draft`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          session_id: sessionId,
          patient_id: patientId || "demo-patient",
          template_id: selectedTemplate.id,
          sections: sectionContent,
          status: "draft"
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success("Draft saved");
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.success("Draft saved (demo mode)");
    } finally {
      setSaving(false);
    }
  };

  // Submit documentation
  const submitDocumentation = async () => {
    if (!selectedTemplate) {
      toast.error("Please select a template first");
      return;
    }
    
    // Check required sections
    const requiredSections = templateSections
      .filter(s => s.required)
      .map(s => s.id);
    const missingSections = requiredSections.filter(id => !sectionContent[id]?.trim());
    
    if (missingSections.length > 0) {
      toast.error(`Missing required sections: ${missingSections.join(", ")}`);
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/rn-voice-command/documentation/submit`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          session_id: sessionId,
          patient_id: patientId || "demo-patient",
          template_id: selectedTemplate.id,
          sections: sectionContent,
          status: "signed"
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success("Documentation submitted and signed");
        setSummaryDialog(true);
      }
    } catch (error) {
      console.error("Error submitting:", error);
      toast.success("Documentation submitted (demo mode)");
      setSummaryDialog(true);
    } finally {
      setSaving(false);
    }
  };

  // Get session summary
  const getSessionSummary = async () => {
    if (!sessionId) return null;
    
    try {
      const res = await fetch(`${API_URL}/api/rn-voice-command/session/${sessionId}/summary`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      console.error("Error getting summary:", error);
    }
    return null;
  };

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate completion percentage
  const getCompletionPercentage = () => {
    if (!templateSections.length) return 0;
    const filled = templateSections.filter(s => sectionContent[s.id]?.trim()).length;
    return Math.round((filled / templateSections.length) * 100);
  };

  // Get section status
  const getSectionStatus = (sectionId, required) => {
    const content = sectionContent[sectionId];
    if (content?.trim()) return "complete";
    if (required) return "incomplete";
    return "empty";
  };

  // Load initial data
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isRecording]);

  return (
    <div className={`fixed inset-0 z-50 ${isDark ? "bg-slate-950" : "bg-gray-50"}`}>
      {/* Header */}
      <div className={`border-b ${isDark ? "border-slate-800 bg-slate-900" : "border-gray-200 bg-white"} px-6 py-3`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-600 flex items-center justify-center">
                <Mic className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  RN Voice Command
                  {sessionActive && (
                    <Badge className="bg-green-500/20 text-green-400 text-xs">Active</Badge>
                  )}
                </h1>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  {patientName || "Select Patient"} • Voice-First Documentation
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setHelpDialog(true)}
              data-testid="help-button"
            >
              <HelpCircle className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              data-testid="close-button"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-65px)]">
        {/* Left Panel - Voice & Transcription */}
        <div className={`w-1/2 border-r ${isDark ? "border-slate-800" : "border-gray-200"} flex flex-col`}>
          {/* Voice Controls */}
          <div className={`p-6 border-b ${isDark ? "border-slate-800" : "border-gray-200"}`}>
            {!sessionActive ? (
              <div className="text-center py-8">
                <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                  <Mic className={`w-10 h-10 ${isDark ? "text-gray-500" : "text-gray-400"}`} />
                </div>
                <h3 className="text-lg font-semibold mb-2">Start RN Command Session</h3>
                <p className={`text-sm mb-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  Begin voice documentation for your patient visit
                </p>
                <Button
                  onClick={startSession}
                  disabled={loading}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="start-session-button"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                  Start Session
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Recording Button */}
                <div className="flex items-center justify-center">
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={loading}
                    className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                      isRecording
                        ? "bg-red-500 animate-pulse shadow-lg shadow-red-500/50"
                        : isDark
                          ? "bg-teal-600 hover:bg-teal-500 shadow-lg shadow-teal-500/30"
                          : "bg-teal-600 hover:bg-teal-500 shadow-lg"
                    }`}
                    data-testid="record-button"
                  >
                    {loading ? (
                      <Loader2 className="w-10 h-10 text-white animate-spin" />
                    ) : isRecording ? (
                      <Square className="w-10 h-10 text-white" />
                    ) : (
                      <Mic className="w-10 h-10 text-white" />
                    )}
                  </button>
                </div>
                
                {/* Recording Duration */}
                {isRecording && (
                  <div className="text-center">
                    <p className="text-2xl font-mono text-red-500">
                      {formatDuration(recordingDuration)}
                    </p>
                    <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                      Recording...
                    </p>
                  </div>
                )}
                
                {/* Instructions */}
                {!isRecording && (
                  <div className="text-center">
                    <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                      Tap to start dictation
                    </p>
                    {activeSection.current && (
                      <Badge className="mt-2 bg-teal-500/20 text-teal-400">
                        Recording to: {activeSection.current}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Template Detection Alert */}
          {templateDetection && !selectedTemplate && (
            <div className={`p-4 border-b ${isDark ? "border-slate-800 bg-purple-500/10" : "border-purple-200 bg-purple-50"}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  <div>
                    <p className="font-medium text-purple-500">Template Detected</p>
                    <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                      {templateDetection.detected_template} ({templateDetection.confidence}% confidence)
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => selectTemplate(templateDetection.detected_template)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  Load Template
                </Button>
              </div>
            </div>
          )}

          {/* Live Transcript */}
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className={`px-6 py-3 flex items-center justify-between ${isDark ? "bg-slate-900/50" : "bg-gray-50"}`}>
              <h3 className="font-semibold flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Live Transcript
              </h3>
              {transcript && (
                <Button variant="ghost" size="sm" onClick={() => setTranscript("")}>
                  <Trash2 className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
            <ScrollArea className="flex-1 p-6">
              {transcript ? (
                <div className={`prose ${isDark ? "prose-invert" : ""} max-w-none`}>
                  <p className={`whitespace-pre-wrap ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    {transcript}
                  </p>
                  {liveTranscript && (
                    <p className="mt-4 text-teal-500 italic border-l-2 border-teal-500 pl-3">
                      Latest: {liveTranscript}
                    </p>
                  )}
                </div>
              ) : (
                <div className={`text-center py-8 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                  <Volume2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Start dictating to see transcript here</p>
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Clarifications & Warnings */}
          {(clarificationNeeded.length > 0 || cmsWarnings.length > 0) && (
            <div className={`p-4 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
              {clarificationNeeded.length > 0 && (
                <div className={`mb-3 p-3 rounded-lg ${isDark ? "bg-yellow-500/10" : "bg-yellow-50"}`}>
                  <p className="font-medium text-yellow-500 text-sm mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Clarification Needed
                  </p>
                  <ul className={`text-sm space-y-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {clarificationNeeded.map((item, idx) => (
                      <li key={idx}>• {item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {cmsWarnings.length > 0 && (
                <div className={`p-3 rounded-lg ${isDark ? "bg-red-500/10" : "bg-red-50"}`}>
                  <p className="font-medium text-red-500 text-sm mb-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    CMS Compliance Warnings
                  </p>
                  <ul className={`text-sm space-y-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {cmsWarnings.map((item, idx) => (
                      <li key={idx}>• {item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Panel - Documentation & Actions */}
        <div className="w-1/2 flex flex-col">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
            <div className={`px-6 py-3 border-b ${isDark ? "border-slate-800" : "border-gray-200"}`}>
              <TabsList className={isDark ? "bg-slate-800" : "bg-gray-100"}>
                <TabsTrigger value="documentation" data-testid="documentation-tab">
                  <FileText className="w-4 h-4 mr-2" />
                  Documentation
                </TabsTrigger>
                <TabsTrigger value="orders" data-testid="orders-tab">
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Orders
                  {draftedOrders.length > 0 && (
                    <Badge className="ml-2 bg-blue-500 text-white text-xs">{draftedOrders.length}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="medications" data-testid="medications-tab">
                  <Pill className="w-4 h-4 mr-2" />
                  Meds
                  {medicationUpdates.length > 0 && (
                    <Badge className="ml-2 bg-green-500 text-white text-xs">{medicationUpdates.length}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="idg" data-testid="idg-tab">
                  <Users className="w-4 h-4 mr-2" />
                  IDG
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Documentation Tab */}
            <TabsContent value="documentation" className="flex-1 flex flex-col mt-0">
              {/* Template Selector */}
              <div className={`p-4 border-b ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                <Label className="text-sm mb-2 block">Visit Template</Label>
                <Select
                  value={selectedTemplate?.id || ""}
                  onValueChange={selectTemplate}
                >
                  <SelectTrigger className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                    <SelectValue placeholder="Select a template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        <div className="flex items-center gap-2">
                          <Badge className={`${TEMPLATE_TYPES[t.type]?.color || "bg-gray-500"} text-white text-xs`}>
                            {t.type}
                          </Badge>
                          {t.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sections */}
              <ScrollArea className="flex-1">
                {selectedTemplate ? (
                  <div className="p-4 space-y-3">
                    {/* Completion Progress */}
                    <div className={`p-3 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Documentation Progress</span>
                        <span className="text-sm font-bold">{getCompletionPercentage()}%</span>
                      </div>
                      <Progress value={getCompletionPercentage()} className="h-2" />
                    </div>

                    {/* Section Cards */}
                    {templateSections.map(section => {
                      const status = getSectionStatus(section.id, section.required);
                      const StatusIcon = SECTION_STATUS[status].icon;
                      const isExpanded = expandedSections[section.id] !== false;
                      
                      return (
                        <Collapsible
                          key={section.id}
                          open={isExpanded}
                          onOpenChange={(open) => setExpandedSections(prev => ({ ...prev, [section.id]: open }))}
                        >
                          <Card className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                            <CollapsibleTrigger className="w-full">
                              <CardHeader className="py-3 px-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <StatusIcon className={`w-4 h-4 ${SECTION_STATUS[status].color}`} />
                                    <CardTitle className="text-sm font-medium">
                                      {section.name}
                                      {section.required && <span className="text-red-500 ml-1">*</span>}
                                    </CardTitle>
                                  </div>
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </div>
                              </CardHeader>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <CardContent className="pt-0 px-4 pb-4">
                                {/* Prompts */}
                                {section.prompts && section.prompts.length > 0 && (
                                  <div className={`text-xs mb-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                                    {section.prompts.join(" ")}
                                  </div>
                                )}
                                <Textarea
                                  value={sectionContent[section.id] || ""}
                                  onChange={(e) => setSectionContent(prev => ({
                                    ...prev,
                                    [section.id]: e.target.value
                                  }))}
                                  onFocus={() => { activeSection.current = section.name; }}
                                  onBlur={() => { activeSection.current = null; }}
                                  placeholder={`Enter ${section.name.toLowerCase()}...`}
                                  className={`min-h-[80px] ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
                                  data-testid={`section-${section.id}`}
                                />
                                <div className="flex justify-end mt-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      activeSection.current = section.name;
                                      startRecording();
                                    }}
                                    disabled={isRecording || !sessionActive}
                                  >
                                    <Mic className="w-3 h-3 mr-1" />
                                    Dictate
                                  </Button>
                                </div>
                              </CardContent>
                            </CollapsibleContent>
                          </Card>
                        </Collapsible>
                      );
                    })}
                  </div>
                ) : (
                  <div className={`text-center py-12 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Select a template to start documenting</p>
                    <p className="text-sm mt-2">Or dictate and let AI detect the template</p>
                  </div>
                )}
              </ScrollArea>

              {/* Documentation Actions */}
              {selectedTemplate && (
                <div className={`p-4 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={saveDraft}
                      disabled={saving}
                      className="flex-1"
                      data-testid="save-draft-button"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Draft
                    </Button>
                    <Button
                      onClick={submitDocumentation}
                      disabled={saving || getCompletionPercentage() < 50}
                      className="flex-1 bg-teal-600 hover:bg-teal-700"
                      data-testid="submit-button"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Sign & Submit
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders" className="flex-1 flex flex-col mt-0">
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  <Button
                    onClick={() => setOrderDialog(true)}
                    disabled={!sessionActive}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="new-order-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Draft New Order
                  </Button>

                  {draftedOrders.length > 0 ? (
                    draftedOrders.map((order, idx) => (
                      <Card key={idx} className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        <CardHeader className="py-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm">
                              {order.order_type} Order
                            </CardTitle>
                            <Badge className="bg-amber-500/20 text-amber-500">
                              Pending MD Review
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                            {order.order_text}
                          </p>
                          {order.controlled_substance_flag && (
                            <Badge className="mt-2 bg-red-500/20 text-red-500">
                              Controlled Substance
                            </Badge>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <div className={`text-center py-8 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                      <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No orders drafted</p>
                      <p className="text-sm mt-1">Say "Create an order for..." to draft</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Medications Tab */}
            <TabsContent value="medications" className="flex-1 flex flex-col mt-0">
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  <Button
                    onClick={() => setMedicationDialog(true)}
                    disabled={!sessionActive}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="new-medication-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Medication Change
                  </Button>

                  {medicationUpdates.length > 0 ? (
                    medicationUpdates.map((med, idx) => (
                      <Card key={idx} className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        <CardHeader className="py-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm capitalize">
                              {med.action}: {med.medication_name}
                            </CardTitle>
                            <Badge className={
                              med.high_risk_flag
                                ? "bg-red-500/20 text-red-500"
                                : "bg-green-500/20 text-green-500"
                            }>
                              {med.high_risk_flag ? "High Risk" : "Standard"}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className={`text-sm space-y-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                            {med.dose && <p>Dose: {med.dose}</p>}
                            {med.frequency && <p>Frequency: {med.frequency}</p>}
                            {med.route && <p>Route: {med.route}</p>}
                            {med.indication && <p>Indication: {med.indication}</p>}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <div className={`text-center py-8 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                      <Pill className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No medication changes</p>
                      <p className="text-sm mt-1">Say "Start medication..." to add</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* IDG Tab */}
            <TabsContent value="idg" className="flex-1 flex flex-col mt-0">
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  <Card className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">Add IDG Note</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        placeholder="Add observations for IDG team..."
                        className={`min-h-[100px] ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
                        data-testid="idg-note-input"
                      />
                      <Button className="w-full mt-3" disabled={!sessionActive}>
                        <Users className="w-4 h-4 mr-2" />
                        Add to IDG Summary
                      </Button>
                    </CardContent>
                  </Card>

                  {idgNotes.length > 0 ? (
                    idgNotes.map((note, idx) => (
                      <Card key={idx} className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        <CardContent className="py-3">
                          <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            {note.idg_summary}
                          </p>
                          {note.discipline_followup && Object.keys(note.discipline_followup).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {Object.keys(note.discipline_followup).map(disc => (
                                <Badge key={disc} variant="outline" className="text-xs">
                                  {disc}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <div className={`text-center py-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                      <p className="text-sm">Say "Update IDG" or "Team needs to know..."</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Order Dialog */}
      <Dialog open={orderDialog} onOpenChange={setOrderDialog}>
        <DialogContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
          <DialogHeader>
            <DialogTitle>Draft Physician Order</DialogTitle>
            <DialogDescription>
              RN-Initiated – Pending MD Review & Signature
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Order Type</Label>
                <Select
                  value={orderForm.order_type}
                  onValueChange={(v) => setOrderForm(prev => ({ ...prev, order_type: v }))}
                >
                  <SelectTrigger className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="medication">Medication</SelectItem>
                    <SelectItem value="lab">Lab</SelectItem>
                    <SelectItem value="imaging">Imaging</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Urgency</Label>
                <Select
                  value={orderForm.urgency}
                  onValueChange={(v) => setOrderForm(prev => ({ ...prev, urgency: v }))}
                >
                  <SelectTrigger className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="routine">Routine</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="stat">STAT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Order Details</Label>
              <Textarea
                value={orderForm.order_text}
                onChange={(e) => setOrderForm(prev => ({ ...prev, order_text: e.target.value }))}
                placeholder="Describe the order..."
                className={isDark ? "bg-slate-800 border-slate-700" : ""}
              />
            </div>
            <div>
              <Label>Clinical Justification</Label>
              <Textarea
                value={orderForm.clinical_justification}
                onChange={(e) => setOrderForm(prev => ({ ...prev, clinical_justification: e.target.value }))}
                placeholder="Reason for order..."
                className={isDark ? "bg-slate-800 border-slate-700" : ""}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderDialog(false)}>Cancel</Button>
            <Button onClick={draftOrder} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Draft Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Medication Dialog */}
      <Dialog open={medicationDialog} onOpenChange={setMedicationDialog}>
        <DialogContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
          <DialogHeader>
            <DialogTitle>Medication Update</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Action</Label>
              <Select
                value={medicationForm.action}
                onValueChange={(v) => setMedicationForm(prev => ({ ...prev, action: v }))}
              >
                <SelectTrigger className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="start">Start New</SelectItem>
                  <SelectItem value="discontinue">Discontinue</SelectItem>
                  <SelectItem value="update">Update Dose</SelectItem>
                  <SelectItem value="add_prn">Add PRN</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Medication Name</Label>
              <Input
                value={medicationForm.medication_name}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, medication_name: e.target.value }))}
                placeholder="Enter medication..."
                className={isDark ? "bg-slate-800 border-slate-700" : ""}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Dose</Label>
                <Input
                  value={medicationForm.dose}
                  onChange={(e) => setMedicationForm(prev => ({ ...prev, dose: e.target.value }))}
                  placeholder="e.g., 5mg"
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
              <div>
                <Label>Frequency</Label>
                <Input
                  value={medicationForm.frequency}
                  onChange={(e) => setMedicationForm(prev => ({ ...prev, frequency: e.target.value }))}
                  placeholder="e.g., BID"
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
              <div>
                <Label>Route</Label>
                <Input
                  value={medicationForm.route}
                  onChange={(e) => setMedicationForm(prev => ({ ...prev, route: e.target.value }))}
                  placeholder="e.g., PO"
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
            </div>
            <div>
              <Label>Indication</Label>
              <Input
                value={medicationForm.indication}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, indication: e.target.value }))}
                placeholder="Diagnosis or symptom..."
                className={isDark ? "bg-slate-800 border-slate-700" : ""}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMedicationDialog(false)}>Cancel</Button>
            <Button onClick={processMedication} disabled={loading} className="bg-green-600 hover:bg-green-700">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Medication
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help Dialog - Voice Commands */}
      <Dialog open={helpDialog} onOpenChange={setHelpDialog}>
        <DialogContent className={`max-w-2xl ${isDark ? "bg-slate-900 border-slate-800" : ""}`}>
          <DialogHeader>
            <DialogTitle>Voice Commands</DialogTitle>
            <DialogDescription>
              Available voice commands for hands-free operation
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-6 py-4">
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-500" />
                  Documentation
                </h4>
                <ul className={`text-sm space-y-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  <li>• "Routine visit" - Load routine visit template</li>
                  <li>• "PRN visit" - Load PRN/symptom template</li>
                  <li>• "HOPE update" - Load HOPE update template</li>
                  <li>• "Pronouncement" - Load death pronouncement template</li>
                  <li>• "Wound care" - Load wound focused template</li>
                </ul>
              </div>
              <Separator />
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-orange-500" />
                  Orders & Medications
                </h4>
                <ul className={`text-sm space-y-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  <li>• "Create an order for..." - Draft physician order</li>
                  <li>• "New physician order..." - Draft physician order</li>
                  <li>• "Start medication..." - Start new medication</li>
                  <li>• "Discontinue medication..." - Discontinue med</li>
                  <li>• "Add PRN med..." - Add PRN medication</li>
                </ul>
              </div>
              <Separator />
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-500" />
                  IDG & Care Plan
                </h4>
                <ul className={`text-sm space-y-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  <li>• "Update IDG" - Add to IDG summary</li>
                  <li>• "Team needs to know..." - Flag for team</li>
                  <li>• "Update care plan" - Start care plan update</li>
                  <li>• "Add new goal" - Add care plan goal</li>
                </ul>
              </div>
              <Separator />
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-gray-500" />
                  Session Control
                </h4>
                <ul className={`text-sm space-y-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  <li>• "Switch to orders" - Navigate to orders</li>
                  <li>• "Go back to documentation" - Return to docs</li>
                  <li>• "Save draft" - Save current documentation</li>
                  <li>• "Submit documentation" - Submit for signature</li>
                  <li>• "Sign note" - Sign and finalize (RN)</li>
                </ul>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button onClick={() => setHelpDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary Dialog */}
      <Dialog open={summaryDialog} onOpenChange={setSummaryDialog}>
        <DialogContent className={`max-w-lg ${isDark ? "bg-slate-900 border-slate-800" : ""}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Documentation Submitted
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className={`p-4 rounded-lg ${isDark ? "bg-green-500/10" : "bg-green-50"}`}>
              <p className="text-green-500 font-medium">Visit note signed and saved</p>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {selectedTemplate?.name || "Visit"} documentation complete
              </p>
            </div>
            
            {draftedOrders.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Orders Drafted ({draftedOrders.length})</h4>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  Pending physician review and signature
                </p>
              </div>
            )}
            
            {medicationUpdates.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Medication Changes ({medicationUpdates.length})</h4>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  Recorded and pending verification
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => { setSummaryDialog(false); onClose?.(); }}>
              Close Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
